import type { Extension, PlanDefinition, PlanDefinitionAction, Quantity } from "@medplum/fhirtypes";
import {
  CATALOG_MODEL_KIND_SYSTEM,
  catalogNameToFhir,
  PLAN_DEFINITION_KIND,
  PROTOCOL_DEFINITION_KIND,
  planDefinitionKind,
} from "@repo/api";
import {
  type AnsweredValue,
  type Availability,
  asMemberId,
  asPlannedTrialId,
  examRef,
  type OccurrenceJustification,
  type ParameterAnswer,
  type ParameterSource,
  type PlannedTrial,
  type ProtocolDefinitionProps,
  procedureRef,
  type UnitValue,
} from "@repo/domain";
import { extensionUrl, UCUM_SYSTEM } from "@repo/fhir-systems";
import { readObservationLevels, writeObservationLevels } from "./observation-level-storage";

/**
 * Where a protocol `PlanDefinition` row keeps what the views read
 * (ADR-0021: views read FHIR rows directly and never import codecs).
 *
 * The `action` array is the timed spine — one container action per step,
 * nesting the occurrences that run concurrently within it — and the untimed
 * exam set is a repeating canonical extension beside it, never in `action`.
 * Everything else FHIR has no slot for (the envelope, a step's offset, an
 * occurrence's id, qualifier, provenance and parameter answers) rides on
 * extensions under the `-v2` spelling, which marks the redesigned catalog's
 * members rather than a version of the protocol model.
 *
 * A row this module cannot read THROWS rather than reading as `""`, `NaN` or
 * `Inactive`: a substituted value is indistinguishable from a real one, so a
 * broken row would silently sit in the catalog looking fine. Callers choose
 * the disposition — `projectProtocolDefinitionRows` collects the failure and
 * surfaces it as a catalog warning, and the booking query drops the row.
 */

const PROTOCOL_ENVELOPE_URL = extensionUrl("protocol-envelope-minutes");
const PROTOCOL_EXAM_URL = extensionUrl("protocol-v2-exam");
const PROTOCOL_STEP_OFFSET_URL = extensionUrl("protocol-v2-step-offset-minutes");
const PROTOCOL_OCCURRENCE_SEQUENCE_URL = extensionUrl("protocol-v2-occurrence-sequence");
const OCCURRENCE_ID_URL = extensionUrl("protocol-v2-occurrence-id");
const OCCURRENCE_QUALIFIER_URL = extensionUrl("protocol-v2-occurrence-qualifier");
const OCCURRENCE_AUTHORED_URL = extensionUrl("protocol-v2-occurrence-authored");
const OCCURRENCE_REQUIRED_BY_URL = extensionUrl("protocol-v2-occurrence-required-by");
const PARAMETER_VALUE_URL = extensionUrl("protocol-v2-parameter-value");
const PLANNED_TRIAL_URL = extensionUrl("protocol-v2-planned-trial");

export {
  OCCURRENCE_AUTHORED_URL,
  OCCURRENCE_ID_URL,
  OCCURRENCE_QUALIFIER_URL,
  OCCURRENCE_REQUIRED_BY_URL,
  PARAMETER_VALUE_URL,
  PLANNED_TRIAL_URL,
  PROTOCOL_ENVELOPE_URL,
  PROTOCOL_EXAM_URL,
  PROTOCOL_OCCURRENCE_SEQUENCE_URL,
  PROTOCOL_STEP_OFFSET_URL,
};

// Deliberately not `PlanDefinition/<id>`: that spelling is a FHIR reference,
// and this is a row locator in a message about a row that could not be read.
function unreadable(resource: PlanDefinition, field: string, value?: unknown): never {
  throw new Error(
    `PlanDefinition row '${resource.id ?? "<unassigned>"}' has no readable ${field}${
      value === undefined ? "" : ` (${JSON.stringify(value)})`
    }`,
  );
}

function extensionsOf(node: { extension?: Extension[] } | undefined, url: string): Extension[] {
  return (node?.extension ?? []).filter((candidate) => candidate.url === url);
}

function extensionOf(node: { extension?: Extension[] } | undefined, url: string): Extension | undefined {
  return extensionsOf(node, url)[0];
}

/**
 * A quantity carrying only display text predates the coded unit and cannot be
 * repaired here, so it reads as unitless rather than as a unit nothing matches.
 */
function unitFrom(quantity: Quantity | undefined): UnitValue | undefined {
  if (!quantity?.code) return undefined;
  return { code: quantity.code, display: quantity.unit ?? quantity.code };
}

// ── parameter answers ──

function answeredValue(resource: PlanDefinition, answer: Extension): AnsweredValue | undefined {
  const options = extensionsOf(answer, "option");
  const freeText = extensionOf(answer, "freeText")?.valueString;
  // Free text alone is a complete Choice answer, so `option` being absent does
  // not rule Choice out — keying only on `option` dropped those answers.
  if (options.length > 0 || freeText !== undefined) {
    return {
      kind: "Choice",
      selected: options.map((option) =>
        asMemberId(option.valueString ?? unreadable(resource, "parameter answer option")),
      ),
      ...(freeText === undefined ? {} : { freeText }),
    };
  }
  const value = extensionOf(answer, "value");
  if (value?.valueQuantity?.value !== undefined) {
    const unit = unitFrom(value.valueQuantity);
    return { kind: "Quantity", value: value.valueQuantity.value, ...(unit === undefined ? {} : { unit }) };
  }
  if (value?.valueInteger !== undefined) return { kind: "Integer", value: value.valueInteger };
  if (value?.valueBoolean !== undefined) return { kind: "Boolean", value: value.valueBoolean };
  if (value?.valueString !== undefined) return { kind: "Text", value: value.valueString };
  return undefined;
}

/**
 * An answer with no source, no parameter or no value is DROPPED rather than
 * thrown on: it names no parameter, so there is nothing to hold it against and
 * nothing is substituted by leaving it out.
 */
function parameterAnswer(resource: PlanDefinition, answer: Extension): ParameterAnswer | undefined {
  const stored = extensionOf(answer, "source")?.valueString;
  const parameterId = extensionOf(answer, "parameterId")?.valueString;
  if (!stored || !parameterId) return undefined;
  const value = answeredValue(resource, answer);
  if (!value) return undefined;
  const source: ParameterSource = stored === "self" ? "self" : asMemberId(stored);
  return { source, parameterId: asMemberId(parameterId), value };
}

// ── occurrences and steps ──

function justifications(action: PlanDefinitionAction): OccurrenceJustification[] {
  const authored = extensionOf(action, OCCURRENCE_AUTHORED_URL) ? [{ kind: "authored" as const }] : [];
  const fulfilled = extensionsOf(action, OCCURRENCE_REQUIRED_BY_URL).flatMap((candidate) =>
    candidate.valueCanonical ? [{ kind: "exam" as const, exam: examRef(candidate.valueCanonical) }] : [],
  );
  return [...authored, ...fulfilled];
}

function occurrence(resource: PlanDefinition, action: PlanDefinitionAction) {
  const observationLevels = readObservationLevels(action.extension);
  // A stored occurrence without an id is not defaulted to a fresh one: the
  // entity would mint an identity a materialized order already cites under a
  // different number. Absent means new, which is what the entity does with it.
  const id = extensionOf(action, OCCURRENCE_ID_URL)?.valueString;
  const qualifier = extensionOf(action, OCCURRENCE_QUALIFIER_URL)?.valueString;
  const plannedTrials = extensionsOf(action, PLANNED_TRIAL_URL).map((trial) => plannedTrial(resource, trial));
  return {
    ...(id === undefined ? {} : { id: asMemberId(id) }),
    definition: procedureRef(action.definitionCanonical ?? unreadable(resource, "occurrence definition")),
    ...(qualifier === undefined ? {} : { qualifier }),
    parameterValues: parameterAnswers(resource, action),
    ...(plannedTrials.length ? { plannedTrials } : {}),
    ...(observationLevels ? { observationLevels } : {}),
    justifications: justifications(action),
  };
}

function parameterAnswers(resource: PlanDefinition, holder: { extension?: Extension[] }): ParameterAnswer[] {
  return extensionsOf(holder, PARAMETER_VALUE_URL).flatMap((answer) => {
    const decoded = parameterAnswer(resource, answer);
    return decoded ? [decoded] : [];
  });
}

function plannedTrial(resource: PlanDefinition, trial: Extension): PlannedTrial {
  return {
    id: asPlannedTrialId(extensionOf(trial, "id")?.valueString || unreadable(resource, "planned trial id")),
    values: parameterAnswers(resource, trial),
  };
}

/**
 * Placement is not optional and has no sensible stand-in: a step with no offset
 * has no position in the visit, and one with no duration reserves nothing.
 */
function steps(resource: PlanDefinition): ProtocolDefinitionProps["steps"] {
  return (resource.action ?? []).map((container) => {
    const offsetMinutes = extensionOf(container, PROTOCOL_STEP_OFFSET_URL)?.valueInteger;
    const durationMinutes = container.timingDuration?.value;
    if (offsetMinutes === undefined || durationMinutes === undefined) unreadable(resource, "step placement");
    return {
      ...(container.title === undefined ? {} : { title: container.title }),
      placement: { offsetMinutes, durationMinutes },
      occurrences: (container.action ?? []).map((action) => occurrence(resource, action)),
    };
  });
}

// ── the row ──

/**
 * `active` and `retired` are the only codes a protocol is ever written with.
 * Every other status — `draft`, `unknown`, absent — is unreadable rather than
 * quietly Inactive, because a row nobody meant to retire would then sit in the
 * catalog looking deliberately switched off.
 */
const STATUS_AVAILABILITY: Readonly<Record<string, Availability>> = { active: "Active", retired: "Inactive" };

function availability(resource: PlanDefinition): Availability {
  return STATUS_AVAILABILITY[resource.status ?? ""] ?? unreadable(resource, "status", resource.status);
}

function revision(resource: PlanDefinition): number {
  const version = Number(resource.version);
  // The domain's integer revision, not `meta.versionId`. A stored value that is
  // not an integer is not rounded into one — a resource whose place in its
  // lineage cannot be read has no honest reading.
  return Number.isInteger(version) ? version : unreadable(resource, "revision", resource.version);
}

/**
 * The stored protocol, as the entity's constructor takes it. The read-side twin
 * of `protocol-definition.codec.ts`'s decode, so `new ProtocolDefinition(...)`
 * does the validating and the deriving here exactly as it does on the server.
 *
 * `envelopeMinutes` is passed through unchecked ON PURPOSE: an absent envelope
 * is the domain's `assertDuration` to refuse, and duplicating that increment
 * and cap rule here would put two answers in the tree.
 */
export function readProtocolDefinition(resource: PlanDefinition): ProtocolDefinitionProps {
  const description = resource.description;
  const highWaterMark = extensionOf(resource, PROTOCOL_OCCURRENCE_SEQUENCE_URL)?.valueInteger;
  return {
    spine: {
      url: resource.url ?? unreadable(resource, "lineage url"),
      version: revision(resource),
      // `date` is the MINT date, stamped once. As-of reconstruction resolves
      // against it rather than `meta.lastUpdated`, which a correction moves.
      mintedAt: resource.date ?? unreadable(resource, "mintedAt"),
      name: resource.title ?? unreadable(resource, "name"),
      ...(description === undefined ? {} : { description }),
      availability: availability(resource),
    },
    envelopeMinutes: extensionOf(resource, PROTOCOL_ENVELOPE_URL)?.valueInteger as number,
    steps: steps(resource),
    exams: extensionsOf(resource, PROTOCOL_EXAM_URL).flatMap((candidate) =>
      candidate.valueCanonical ? [examRef(candidate.valueCanonical)] : [],
    ),
    ...(highWaterMark === undefined ? {} : { occurrenceHighWaterMark: highWaterMark }),
  };
}

// ── the optimistic writer ──

/**
 * The inverse of `readProtocolDefinition`, for optimistic rows only.
 *
 * An optimistic row is read back through the SAME projection a synced row
 * takes, so it has to be written in the same storage shape — any difference
 * reads as a flicker when the server row lands. This is the read side's twin of
 * `protocol-definition.codec.ts`'s encode, and the storage test round-trips a
 * capture of that encoder through both directions to prove the two agree.
 */
function answerToExtension(answer: ParameterAnswer): Extension {
  const value = answer.value;
  const common: Extension[] = [
    { url: "source", valueString: answer.source },
    { url: "parameterId", valueString: answer.parameterId },
  ];
  switch (value.kind) {
    case "Quantity":
      return {
        url: PARAMETER_VALUE_URL,
        extension: [
          ...common,
          {
            url: "value",
            valueQuantity: {
              value: value.value,
              ...(value.unit === undefined
                ? {}
                : { unit: value.unit.display, system: UCUM_SYSTEM, code: value.unit.code }),
            },
          },
        ],
      };
    case "Integer":
      return { url: PARAMETER_VALUE_URL, extension: [...common, { url: "value", valueInteger: value.value }] };
    case "Boolean":
      return { url: PARAMETER_VALUE_URL, extension: [...common, { url: "value", valueBoolean: value.value }] };
    case "Text":
      return { url: PARAMETER_VALUE_URL, extension: [...common, { url: "value", valueString: value.value }] };
    case "Choice":
      return {
        url: PARAMETER_VALUE_URL,
        extension: [
          ...common,
          ...value.selected.map((option) => ({ url: "option", valueString: String(option) })),
          ...(value.freeText === undefined ? [] : [{ url: "freeText", valueString: value.freeText }]),
        ],
      };
  }
}

function justificationsToExtensions(justifications: readonly OccurrenceJustification[]): Extension[] {
  return justifications.map((justification) =>
    justification.kind === "authored"
      ? { url: OCCURRENCE_AUTHORED_URL, valueBoolean: true }
      : { url: OCCURRENCE_REQUIRED_BY_URL, valueCanonical: String(justification.exam) },
  );
}

type WrittenOccurrence = ProtocolDefinitionProps["steps"][number]["occurrences"][number];

function occurrenceToAction(occurrence: WrittenOccurrence): PlanDefinitionAction {
  return {
    definitionCanonical: String(occurrence.definition),
    extension: [
      // An occurrence with no id is written WITHOUT one, never with a minted
      // stand-in: absent means new, which is what the entity does with it.
      ...(occurrence.id === undefined ? [] : [{ url: OCCURRENCE_ID_URL, valueString: String(occurrence.id) }]),
      ...(occurrence.qualifier === undefined
        ? []
        : [{ url: OCCURRENCE_QUALIFIER_URL, valueString: occurrence.qualifier }]),
      ...justificationsToExtensions(occurrence.justifications ?? []),
      ...(occurrence.parameterValues ?? []).map(answerToExtension),
      ...writeObservationLevels(occurrence.observationLevels),
      ...(occurrence.plannedTrials ?? []).map((trial) => ({
        url: PLANNED_TRIAL_URL,
        extension: [{ url: "id", valueString: trial.id }, ...trial.values.map(answerToExtension)],
      })),
    ],
  };
}

export function writeProtocolDefinitionRow(props: ProtocolDefinitionProps): PlanDefinition {
  const { spine } = props;
  return {
    resourceType: "PlanDefinition",
    meta: { tag: [{ system: CATALOG_MODEL_KIND_SYSTEM, code: PROTOCOL_DEFINITION_KIND }] },
    url: spine.url,
    version: String(spine.version),
    ...catalogNameToFhir(spine.name),
    ...(spine.description === undefined ? {} : { description: spine.description }),
    // The MINT date, stamped once. A correction moves `meta.lastUpdated`, so
    // resolving as-of against that would make an old revision look minted on
    // the day someone fixed a typo.
    date: spine.mintedAt,
    status: spine.availability === "Active" ? "active" : "retired",
    // Always the kind coding: a protocol used to be "whatever is left once
    // programs are excluded", which is not something a search can ask for.
    type: planDefinitionKind(PLAN_DEFINITION_KIND.protocol),
    ...(props.steps.length === 0
      ? {}
      : {
          action: props.steps.map((step) => ({
            ...(step.title === undefined ? {} : { title: step.title }),
            extension: [{ url: PROTOCOL_STEP_OFFSET_URL, valueInteger: step.placement.offsetMinutes }],
            timingDuration: {
              value: step.placement.durationMinutes,
              unit: "min",
              system: UCUM_SYSTEM,
              code: "min",
            },
            action: step.occurrences.map(occurrenceToAction),
          })),
        }),
    extension: [
      { url: PROTOCOL_ENVELOPE_URL, valueInteger: props.envelopeMinutes },
      ...props.exams.map((exam) => ({ url: PROTOCOL_EXAM_URL, valueCanonical: String(exam) })),
      ...(props.occurrenceHighWaterMark === undefined
        ? []
        : [{ url: PROTOCOL_OCCURRENCE_SEQUENCE_URL, valueInteger: props.occurrenceHighWaterMark }]),
    ],
  };
}
